import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Select, Input } from '@components';
import { AnalyticsFilters, AnalyticsFiltersData } from '../types';

interface AnalyticsFiltersProps {
  filters: AnalyticsFilters;
  filtersData: AnalyticsFiltersData | null;
  onFiltersChange: (filters: AnalyticsFilters) => void;
}

export const AnalyticsFiltersComponent: React.FC<AnalyticsFiltersProps> = ({
  filters,
  filtersData,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  const handleDateRangeChange = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    onFiltersChange({
      ...filters,
      dateRange: stringValue as AnalyticsFilters['dateRange'],
    });
  };

  const handleCustomDateChange = (field: 'customFromDate' | 'customToDate', value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value,
    });
  };

  const handleAgentChange = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    onFiltersChange({
      ...filters,
      agent: stringValue,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('analytics.filterBy')}
        </h2>
      </div>

      <Card>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <label
                htmlFor="dateRange"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('analytics.dateRange')}
              </label>
              <Select
                id="dateRange"
                value={filters.dateRange}
                onChange={handleDateRangeChange}
                options={[
                  { value: '7d', label: t('analytics.last7Days') },
                  { value: '30d', label: t('analytics.last30Days') },
                  { value: '90d', label: t('analytics.last90Days') },
                  { value: 'custom', label: t('analytics.custom') },
                ]}
              />
            </div>

            <div className="flex-1">
              <label
                htmlFor="agent"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('analytics.agent')}
              </label>
              <Select
                id="agent"
                value={filters.agent}
                onChange={handleAgentChange}
                options={[
                  { value: '', label: t('analytics.allAgents') },
                  ...(filtersData?.agents || []),
                ]}
              />
            </div>
          </div>

          {filters.dateRange === 'custom' && (
            <div className="flex gap-4 mt-4">
              <div className="flex-1">
                <label
                  htmlFor="fromDate"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('analytics.fromDate')}
                </label>
                <Input
                  id="fromDate"
                  type="date"
                  value={filters.customFromDate}
                  onChange={e => handleCustomDateChange('customFromDate', e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="toDate"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('analytics.toDate')}
                </label>
                <Input
                  id="toDate"
                  type="date"
                  value={filters.customToDate}
                  onChange={e => handleCustomDateChange('customToDate', e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
